import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import ResumeCard from "../../components/Resume/ResumeCard";
import * as resumeService from "../../services/resumeService";
import { toast } from "react-toastify";

export default function Resumes() {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState("");
  const [sort, setSort] = useState("date");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await resumeService.getAllResumes(1, 100);
        setResumes(r.resumes || []);
      } catch {
        toast.error("Failed to load resumes");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let arr = [...resumes];
    if (search.trim())
      arr = arr.filter((r) =>
        (r.title || "").toLowerCase().includes(search.toLowerCase())
      );
    if (templateFilter)
      arr = arr.filter((r) => r.templateType === templateFilter);
    if (sort === "date")
      arr.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    if (sort === "title")
      arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    if (sort === "score")
      arr.sort((a, b) => (b.atsScore || 0) - (a.atsScore || 0));
    return arr;
  }, [resumes, search, templateFilter, sort]);

  function toggleSelectAll() {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map((r) => r._id));
  }

  async function bulkDelete() {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} resumes?`)) return;
    for (const id of selected) {
      // eslint-disable-next-line no-await-in-loop
      await resumeService.deleteResume(id);
    }
    setResumes((rs) => rs.filter((r) => !selected.includes(r._id)));
    setSelected([]);
    toast.success("Deleted selected resumes");
  }

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner fullScreen />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">My Resumes</h1>
          <Button onClick={() => (window.location.href = "/resumes/new")}>
            Create New Resume
          </Button>
        </header>

        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <input
              className="border rounded px-3 py-2"
              placeholder="Search by title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="border rounded px-3 py-2"
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
            >
              <option value="">All Templates</option>
              {["modern", "professional", "creative", "minimal"].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              className="border rounded px-3 py-2"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="date">Sort by Date</option>
              <option value="title">Sort by Title</option>
              <option value="score">Sort by Score</option>
            </select>
            <div className="flex items-center gap-2">
              <button
                className={`px-2 py-1 rounded ${
                  view === "grid" ? "bg-gray-200" : ""
                }`}
                onClick={() => setView("grid")}
              >
                Grid
              </button>
              <button
                className={`px-2 py-1 rounded ${
                  view === "list" ? "bg-gray-200" : ""
                }`}
                onClick={() => setView("list")}
              >
                List
              </button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={
                  selected.length === filtered.length && filtered.length > 0
                }
                onChange={toggleSelectAll}
              />
              Select All
            </label>
            <Button
              variant="ghost"
              onClick={bulkDelete}
              disabled={!selected.length}
            >
              Delete Selected
            </Button>
          </div>
        </Card>

        {!filtered.length ? (
          <Card>
            <div className="text-center py-10">
              <div className="h-24 bg-gray-100 rounded mx-auto w-48" />
              <h2 className="mt-4 text-lg font-semibold">
                Create Your First Resume
              </h2>
              <p className="text-sm text-gray-600">
                Start building your ATS-optimized resume in minutes.
              </p>
              <div className="mt-3">
                <Button onClick={() => (window.location.href = "/resumes/new")}>
                  Get Started
                </Button>
              </div>
            </div>
          </Card>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => (
              <div key={r._id}>
                <label className="flex items-center gap-2 text-xs mb-1">
                  <input
                    type="checkbox"
                    checked={selected.includes(r._id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelected((s) => [...s, r._id]);
                      else setSelected((s) => s.filter((id) => id !== r._id));
                    }}
                  />
                  <span>Select</span>
                </label>
                <ResumeCard
                  resume={r}
                  onDelete={(id) =>
                    setResumes((rs) => rs.filter((x) => x._id !== id))
                  }
                  onUpdated={async () => {
                    const ref = await resumeService.getAllResumes(1, 100);
                    setResumes(ref.resumes || []);
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Select</th>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2">Template</th>
                  <th className="text-left px-4 py-2">ATS</th>
                  <th className="text-left px-4 py-2">Version</th>
                  <th className="text-left px-4 py-2">Updated</th>
                  <th className="text-left px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r._id} className="border-t">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selected.includes(r._id)}
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelected((s) => [...s, r._id]);
                          else
                            setSelected((s) => s.filter((id) => id !== r._id));
                        }}
                      />
                    </td>
                    <td className="px-4 py-2">{r.title}</td>
                    <td className="px-4 py-2">{r.templateType}</td>
                    <td className="px-4 py-2">{r.atsScore || 0}%</td>
                    <td className="px-4 py-2">{r.version || 1}</td>
                    <td className="px-4 py-2">
                      {new Date(r.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          (window.location.href = `/resumes/${r._id}/edit`)
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          (window.location.href = `/resumes/${r._id}/preview`)
                        }
                      >
                        Preview
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
